import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Users, Server, Activity, DollarSign, Search, PenSquare, Eye, EyeOff, Edit3, Trash2, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useMyBlogPosts, useUpdateBlogPost, useDeleteBlogPost } from '@/hooks/useBlog';

export function Admin() {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [instancesPage, setInstancesPage] = useState(1);
  const usersLimit = 20;
  const instancesLimit = 20;

  // Determine which section to show based on route
  const currentSection = location.pathname === '/admin/instances'
    ? 'instances'
    : location.pathname === '/admin/blog'
    ? 'blog'
    : 'users';

  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const { data } = await api.get('/admin/stats');
      return data;
    },
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users', search, usersPage, usersLimit],
    queryFn: async () => {
      const { data } = await api.get(`/admin/users?search=${search}&page=${usersPage}&limit=${usersLimit}`);
      return data;
    },
    enabled: currentSection === 'users',
  });

  const { data: instancesData, isLoading: instancesLoading } = useQuery({
    queryKey: ['admin-instances', instancesPage, instancesLimit],
    queryFn: async () => {
      const { data } = await api.get(`/admin/instances?page=${instancesPage}&limit=${instancesLimit}`);
      return data;
    },
    enabled: currentSection === 'instances',
  });

  const { data: blogData, isLoading: blogLoading } = useMyBlogPosts({ page: 1, limit: 50 });
  const updatePost = useUpdateBlogPost();
  const deletePost = useDeleteBlogPost();

  const toggleUser = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/admin/users/${id}/toggle`, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const handleToggleBlogStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    await updatePost.mutateAsync({ id, data: { status: newStatus as 'draft' | 'published' } });
  };

  const handleDeleteBlog = async (id: string) => {
    if (window.confirm(t('blogPage.deleteConfirm'))) {
      await deletePost.mutateAsync(id);
    }
  };

  const userStatCards = [
    { title: t('admin.totalUsers'), value: stats?.totalUsers || 0, icon: Users, color: 'text-blue-500 bg-blue-500/10' },
    { title: t('admin.totalInstances'), value: stats?.totalInstances || 0, icon: Server, color: 'text-emerald-500 bg-emerald-500/10' },
    { title: t('admin.activeInstances'), value: stats?.activeInstances || 0, icon: Activity, color: 'text-purple-500 bg-purple-500/10' },
    { title: t('admin.monthlyRevenue'), value: formatCurrency(stats?.monthlyRevenueCents || 0), icon: DollarSign, color: 'text-orange-500 bg-orange-500/10' },
  ];

  const instanceStatCards = [
    { title: 'Total Instances', value: stats?.totalInstances || 0, icon: Server, color: 'text-emerald-500 bg-emerald-500/10' },
    { title: 'Running', value: stats?.activeInstances || 0, icon: Activity, color: 'text-green-500 bg-green-500/10' },
    { title: 'Provisioning', value: (stats?.totalInstances || 0) - (stats?.activeInstances || 0), icon: Activity, color: 'text-yellow-500 bg-yellow-500/10' },
    { title: 'Monthly Revenue', value: formatCurrency(stats?.monthlyRevenueCents || 0), icon: DollarSign, color: 'text-orange-500 bg-orange-500/10' },
  ];

  const totalUsersPages = Math.ceil((usersData?.total || 0) / usersLimit);
  const totalInstancesPages = Math.ceil((instancesData?.total || 0) / instancesLimit);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">
        {currentSection === 'users' && t('admin.users')}
        {currentSection === 'instances' && t('admin.instances')}
        {currentSection === 'blog' && 'Blog Posts'}
      </h1>

      {/* Stats */}
      {currentSection === 'users' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {userStatCards.map((stat, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <div className={`p-2 rounded-lg ${stat.color}`}>
                    <stat.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {currentSection === 'instances' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {instanceStatCards.map((stat, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <div className={`p-2 rounded-lg ${stat.color}`}>
                    <stat.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Users Section */}
      {currentSection === 'users' && (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setUsersPage(1); // Reset to first page when searching
              }}
              className="pl-9"
            />
          </div>
          <Card>
            <CardContent className="p-0">
              {usersLoading ? (
                <div className="p-8 space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse flex gap-4">
                      <div className="h-5 w-1/4 bg-muted rounded" />
                      <div className="h-5 w-1/3 bg-muted rounded" />
                      <div className="h-5 w-1/6 bg-muted rounded" />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-3 font-medium">{t('admin.name')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.email')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.plan')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.instances')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.status')}</th>
                          <th className="text-right p-3 font-medium">{t('admin.actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {usersData?.users?.map((user: any) => (
                          <tr key={user.id} className="hover:bg-accent/50">
                            <td className="p-3 font-medium">{user.name}</td>
                            <td className="p-3 text-muted-foreground">{user.email}</td>
                            <td className="p-3">
                              <Badge variant="secondary" className="capitalize">{user.plan}</Badge>
                            </td>
                            <td className="p-3">{user.instanceCount || user.instance_count || 0}</td>
                            <td className="p-3">
                              <Badge variant={user.isActive || user.is_active ? 'success' : 'destructive'}>
                                {user.isActive || user.is_active ? t('admin.active') : t('admin.inactive')}
                              </Badge>
                            </td>
                            <td className="p-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleUser.mutate({ id: user.id, isActive: !(user.isActive || user.is_active) })}
                              >
                                {user.isActive || user.is_active ? t('admin.deactivate') : t('admin.activate')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {!usersData?.users?.length && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-muted-foreground">
                              {t('admin.noUsersFound')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalUsersPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <div className="text-sm text-muted-foreground">
                        Showing {((usersPage - 1) * usersLimit) + 1} to {Math.min(usersPage * usersLimit, usersData?.total || 0)} of {usersData?.total || 0} users
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={usersPage <= 1}
                          onClick={() => setUsersPage(p => p - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <div className="text-sm text-muted-foreground">
                          Page {usersPage} of {totalUsersPages}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={usersPage >= totalUsersPages}
                          onClick={() => setUsersPage(p => p + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Instances Section */}
      {currentSection === 'instances' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-0">
              {instancesLoading ? (
                <div className="p-8 space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse flex gap-4">
                      <div className="h-5 w-1/4 bg-muted rounded" />
                      <div className="h-5 w-1/3 bg-muted rounded" />
                      <div className="h-5 w-1/6 bg-muted rounded" />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-3 font-medium">{t('admin.name')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.user')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.provider')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.status')}</th>
                          <th className="text-left p-3 font-medium">{t('admin.created')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {instancesData?.instances?.map((inst: any) => (
                          <tr key={inst.id} className="hover:bg-accent/50">
                            <td className="p-3 font-medium">{inst.name}</td>
                            <td className="p-3 text-muted-foreground">
                              {inst.userName || inst.user_name} ({inst.userEmail || inst.user_email})
                            </td>
                            <td className="p-3">{inst.provider} / {inst.region}</td>
                            <td className="p-3">
                              <Badge variant={
                                inst.status === 'running' ? 'success' :
                                inst.status === 'error' ? 'destructive' :
                                'secondary'
                              } className="capitalize">
                                {inst.status}
                              </Badge>
                            </td>
                            <td className="p-3 text-muted-foreground">{formatDate(inst.createdAt || inst.created_at)}</td>
                          </tr>
                        ))}
                        {!instancesData?.instances?.length && (
                          <tr>
                            <td colSpan={5} className="p-8 text-center text-muted-foreground">
                              {t('admin.noInstancesFound')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalInstancesPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <div className="text-sm text-muted-foreground">
                        Showing {((instancesPage - 1) * instancesLimit) + 1} to {Math.min(instancesPage * instancesLimit, instancesData?.total || 0)} of {instancesData?.total || 0} instances
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={instancesPage <= 1}
                          onClick={() => setInstancesPage(p => p - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <div className="text-sm text-muted-foreground">
                          Page {instancesPage} of {totalInstancesPages}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={instancesPage >= totalInstancesPages}
                          onClick={() => setInstancesPage(p => p + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Blog Section */}
      {currentSection === 'blog' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Manage all blog posts
            </p>
            <Link to="/blog/create">
              <Button className="gap-2" size="sm">
                <Plus className="h-4 w-4" />
                Create Post
              </Button>
            </Link>
          </div>

          <Card>
            <CardContent className="p-0">
              {blogLoading ? (
                <div className="p-8 space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse flex items-center gap-4">
                      <div className="h-16 w-24 bg-muted rounded" />
                      <div className="flex-1 space-y-2">
                        <div className="h-5 w-2/3 bg-muted rounded" />
                        <div className="h-4 w-1/3 bg-muted rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : blogData && blogData.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Post</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Views</th>
                        <th className="text-left p-3 font-medium">Comments</th>
                        <th className="text-left p-3 font-medium">Created</th>
                        <th className="text-right p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {blogData.data.map((post) => (
                        <tr key={post.id} className="hover:bg-accent/50">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {post.coverImage ? (
                                <img
                                  src={post.coverImage}
                                  alt=""
                                  className="h-12 w-16 rounded object-cover shrink-0"
                                />
                              ) : (
                                <div className="h-12 w-16 rounded bg-gradient-to-br from-emerald-500 to-teal-500 shrink-0 flex items-center justify-center">
                                  <PenSquare className="h-5 w-5 text-white/50" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <Link
                                  to={`/blog/${post.slug}`}
                                  className="font-medium hover:text-emerald-600 line-clamp-1 block"
                                >
                                  {post.title}
                                </Link>
                                <p className="text-xs text-muted-foreground line-clamp-1">
                                  {post.excerpt}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge
                              variant={post.status === 'published' ? 'success' : 'secondary'}
                              className="capitalize"
                            >
                              {post.status}
                            </Badge>
                          </td>
                          <td className="p-3">{post.viewCount}</td>
                          <td className="p-3">{post.commentCount}</td>
                          <td className="p-3 text-muted-foreground">
                            {formatDate(post.createdAt)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleToggleBlogStatus(post.id, post.status)}
                                title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                              >
                                {post.status === 'published' ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                              <Link to={`/blog/edit/${post.id}`}>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteBlog(post.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                    <PenSquare className="h-8 w-8 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-medium mb-1">No blog posts yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create your first blog post to get started
                  </p>
                  <Link to="/blog/create">
                    <Button className="gap-2">
                      <Plus className="h-4 w-4" />
                      Create Post
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
